/**
 * The garden's frost and freeze dates, with the printed planner's own words
 * for them, for the Weather page's frost card.
 *
 * Dates and wording both come from the server so the app, the planner and the
 * planting windows cannot drift apart: nothing here is assembled in the client.
 * The words are in the gardener's language (frostCard.* rows), and the frost and
 * freeze lines carry their colour (tokens).
 *
 * Endpoint: GET /weather/frost-outlook?zip= → FrostOutlook (its `success`
 * included). Who uses it: features/weather FrostOutlook, which caches it
 * under ['frost-outlook', zip] for QUERY_STALE_TIME_MS (the dates move only
 * with the station's normals). Auth: the signed-in token; any postcode may
 * be asked about.
 *
 * @module api/frostOutlook
 */

import { apiClient } from './apiClient';
import type { VerdictToken } from '@/shared/theme/verdictTokens';

/** One season's frost dates, earliest to latest as the planner orders them (a range across New Year included). */
export interface FrostSeason {
  /** The early date (1 year in 10), as the server words it. */
  early: string;
  /** The typical date. */
  typical: string;
  /** The late date (9 years in 10). */
  late: string;
  /** "Could be as early as Apr 10 or as late as May 7" */
  range: string;
}

/** One season as the page shows it, with which season it is and how far off. */
export interface FrostSeasonEntry extends FrostSeason {
  /** Spring's last frost, or fall's first. */
  key: 'spring' | 'fall';
  /** The planner's label, e.g. "First fall frost:". */
  label: string;
  /** Days until the typical date, so the nearer season can lead. */
  daysAway: number;
  /**
   * The freeze (32°F) of the same season, where the station records one.
   * The frost above is 36°F: the reading that nips tender leaves. The freeze
   * is the one that ends tender plants.
   */
  freeze: (FrostSeason & { daysAway: number }) | null;
  /**
   * Where the season stands once its usual window has passed (B144): the server's line ("No first frost yet · later than
   * usual (usually by Oct 31)", or "The usual first-frost window ended Oct 31" when the weather is not kept), drawn as sent.
   * Null in every other case; the dates stay all year.
   */
  status: { text: string } | null;
}

/**
 * The garden's frost dates, with the planner's own explanation of them.
 *
 * Both the dates and the wording come from the server so that the app, the
 * printed planner and the planting windows cannot drift apart. Nothing here is
 * assembled in the client.
 */
export interface FrostOutlook {
  /** The server's success flag, passed through. */
  success: boolean;
  /** True where the place has no frost season by the planner's one frost check (the copy differs entirely, and no
   *  dates are sent). */
  frostFree: boolean;
  /** The weather station the dates are from, and its page when it has one. */
  station: { id: string | null; name: string; url: string | null };
  /** The last spring frost; null where the station has none. */
  lastFrost: FrostSeason | null;
  /** The first fall frost; null where the station has none. */
  firstFrost: FrostSeason | null;
  /** Both seasons, the nearer one first. Ordered by the server. */
  seasons: FrostSeasonEntry[];
  /**
   * The temperatures the dates are for, °F: NOAA's 36°F frost and 32°F freeze, the planner's.
   */
  thresholds: { frostF: number; freezeF: number };
  /**
   * The colour the frost and freeze lines are drawn in (the server's condition_colour table); null for the ink.
   */
  tokens: { frost: VerdictToken | null; freeze: VerdictToken | null };
  /** The planner's words for the card, ready to show. */
  copy: {
    title: string;
    lastLabel: string;
    firstLabel: string;
    typicalNote: string;
    paragraph: string;
    disclaimer: string | null;
  };
}

/**
 * Frost dates for one postal code.
 *
 * @param zip - The garden's postal code; the same one the outlook is drawn for.
 * @returns The dates and their wording.
 */
export async function getFrostOutlook(zip: string): Promise<FrostOutlook> {
  const response = await apiClient.get<FrostOutlook>('/weather/frost-outlook', {
    params: { zip },
  });
  return response.data;
}
